/**
 * Typed client for the KDS OTA worker (`kds-ota` at
 * `updates.tovrik.dev` (dev) / `updates.tovrik.com` (prod)).
 *
 * ## Auth — a per-HUMAN exchanged token, NOT a pasted shared secret
 *
 * (S5 `kds-ota-human-auth-retire-shared-token`.) This client used to read a
 * long-lived `kds_ota_admin_token` out of localStorage — the operator pasted
 * the worker secret `KDS_OTA_ADMIN_TOKEN` into their browser by hand. That
 * meant every OTA action was recorded as the anonymous actor `admin`, the
 * secret sat in localStorage indefinitely, and revoking one person meant
 * rotating it for everyone. All three are gone:
 *
 *   1. The operator's PLATFORM session (the same one AuthGate proves) is
 *      exchanged at kds-api's `POST /api/v1/ota/operator-token` — RFC 8693
 *      OAuth 2.0 Token Exchange — for a ≤5-minute bearer carrying their
 *      user id.
 *   2. That bearer goes to kds-ota, which verifies it back through kds-api
 *      over the service binding and attributes the mutation to the person.
 *   3. On a 401 the token is re-minted ONCE and the call retried — that
 *      covers the expiry race without ever looping.
 *
 * Admission is default-deny: the exchange only mints for a session whose
 * `platform_roles` includes `tovrik_admin`, so a merchant operator gets a
 * clean 403 message instead of a broken page.
 *
 * The exchange is same-origin under the shell mount (which is how the edge
 * injects the identity assertion kds-api verifies) — `resolveKdsApiBase()`
 * owns that resolution, so this module never hardcodes a host.
 *
 * Default OTA base URL is `https://updates.tovrik.dev` (dev) /
 * `https://updates.tovrik.com` (prod), picked at build time via
 * `VITE_OTA_API_URL`. Falls back to dev when unset. (Apex move P3 Slice 2,
 * 2026-07-05: dev default flipped from updates.tovriktech.dev.)
 *
 * Network errors and non-2xx are normalised into thrown Errors with the
 * worker's `error`/`message` payload as the message — pages catch and surface
 * via their existing error UIs.
 */

import { resolveKdsApiBase } from './api';

const OTA_BASE = (import.meta.env.VITE_OTA_API_URL as string | undefined)
  ?? 'https://updates.tovrik.dev';

/** kds-api path that exchanges the platform session for an OTA-operator token. */
const OPERATOR_TOKEN_PATH = '/api/v1/ota/operator-token';

/** Re-mint this many ms before the server-stated expiry (clock skew + flight time). */
const REFRESH_SKEW_MS = 15_000;

let cachedToken: { token: string; expiresAtMs: number } | null = null;

/** Test seam + sign-out hook: drop the in-memory token. */
export function __resetOtaOperatorToken(): void {
  cachedToken = null;
}

async function errorMessage(res: Response): Promise<string> {
  let message = `${res.status} ${res.statusText}`;
  try {
    const body = await res.json() as { error?: string; message?: string };
    message = body.message ?? body.error ?? message;
  } catch { /* response wasn't JSON */ }
  return message;
}

/** RFC 8693 exchange. Throws with the server's message on 401/403/503. */
async function mintOperatorToken(): Promise<string> {
  const res = await fetch(`${resolveKdsApiBase()}${OPERATOR_TOKEN_PATH}`, {
    method: 'POST',
    // The platform session cookie is what is being exchanged.
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    cachedToken = null;
    throw new Error(await errorMessage(res));
  }
  const body = await res.json() as { access_token: string; expires_in?: number };
  const ttlMs = Math.max(0, (body.expires_in ?? 300) * 1000 - REFRESH_SKEW_MS);
  cachedToken = { token: body.access_token, expiresAtMs: Date.now() + ttlMs };
  return body.access_token;
}

async function operatorToken(forceRefresh = false): Promise<string> {
  if (!forceRefresh && cachedToken && cachedToken.expiresAtMs > Date.now()) {
    return cachedToken.token;
  }
  cachedToken = null;
  return mintOperatorToken();
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const send = (token: string) => fetch(`${OTA_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });

  let res = await send(await operatorToken());
  // Exactly ONE retry: the only 401 a valid operator can hit is an expired
  // token. Retrying more than once would turn a genuine authz problem into a
  // mint loop.
  if (res.status === 401) {
    res = await send(await operatorToken(true));
  }
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json() as Promise<T>;
}

// ─── Types (mirror kds-ota worker types) ─────────────────────────

export type Channel = 'production' | 'beta' | 'alpha';

export interface ReleaseSummary {
  id: string;
  version: string;
  channel: Channel;
  bundle_size: number;
  bundle_sha256: string;
  bundle_signature: string;
  min_native_version: string;
  notes: string | null;
  created_at: number;
  created_by: string | null;
  promoted_at: number | null;
  rolled_back_at: number | null;
  active: 0 | 1;
  device_count: number;
  install_success_rate: number | null;
  rollback_count: number;
}

export interface DeviceSummary {
  id: string;
  store_id: string;
  name: string;
  device_type: string | null;
  api_key_prefix: string;
  created_at: number;
  last_seen_at: number | null;
  revoked_at: number | null;
  channel: Channel;
  pinned_version: string | null;
  current_version: string | null;
}

// ─── API methods ──────────────────────────────────────────────────

export const otaApi = {
  async listReleases(channel?: Channel): Promise<ReleaseSummary[]> {
    const q = channel ? `?channel=${encodeURIComponent(channel)}` : '';
    const result = await request<{ releases: ReleaseSummary[] }>(`/api/v1/releases${q}`);
    return result.releases;
  },

  async promote(version: string, toChannel: Channel): Promise<{ id: string; version: string; channel: Channel }> {
    return request('/api/v1/releases/promote', {
      method: 'POST',
      body: JSON.stringify({ version, to_channel: toChannel }),
    });
  },

  async rollback(id: string): Promise<{ ok: true; id: string }> {
    return request(`/api/v1/releases/${encodeURIComponent(id)}/rollback`, { method: 'POST' });
  },

  async listDevices(): Promise<DeviceSummary[]> {
    const result = await request<{ devices: DeviceSummary[] }>('/api/v1/devices');
    return result.devices;
  },

  async setDeviceChannel(deviceId: string, channel: Channel): Promise<{ ok: true; device_id: string; channel: Channel }> {
    return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/channel`, {
      method: 'POST',
      body: JSON.stringify({ channel }),
    });
  },

  async pinDeviceVersion(deviceId: string, version: string | null): Promise<{ ok: true; device_id: string; pinned_version: string | null }> {
    return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/pin-version`, {
      method: 'POST',
      body: JSON.stringify({ version }),
    });
  },
};

/** Format a unix-ms timestamp as a relative-time string (admin-friendly). */
export function relativeTime(unixMs: number | null): string {
  if (!unixMs) return 'never';
  const diff = Date.now() - unixMs;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

/** Format bytes with a human-readable suffix. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
